import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { WarningIcon } from './icons';

const fmtAmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** Shown when the user paid for a card top-up but the bank/provider could not
 *  credit the card (e.g. merchant float empty). Payment is already consumed —
 *  support must credit manually. */
export function CardTopUpManualSheet({
  open,
  onClose,
  telegramId,
  at,
  amount,
  supportTelegram,
}: {
  open: boolean;
  onClose: () => void;
  telegramId: string | number | null;
  /** ISO date or epoch ms of the failed attempt. */
  at: string | number | Date;
  amount?: number | null;
  supportTelegram?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const dateLabel = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(at));

  const idLabel = telegramId != null ? String(telegramId) : '—';

  const copyId = () => {
    if (telegramId == null) return;
    void navigator.clipboard.writeText(String(telegramId)).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => {},
    );
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t('cards.topUpManualTitle')}>
      <div className="mt-3 flex flex-col gap-4">
        <div className="flex items-start gap-2.5 rounded-[16px] bg-[#fff3f2] px-3.5 py-3">
          <WarningIcon size={18} className="mt-0.5 shrink-0 text-[#ff3b30]" />
          <p className="text-[14px] leading-[1.45] text-ink">{t('cards.topUpManualBody')}</p>
        </div>

        <div className="flex flex-col gap-0 overflow-hidden rounded-[16px] bg-[#f6f7f8]">
          <DetailRow
            label={t('profile.telegramId')}
            value={copied ? t('profile.idCopied') : idLabel}
            onClick={telegramId != null ? copyId : undefined}
            accent={copied}
          />
          <DetailRow label={t('cards.topUpManualDate')} value={dateLabel} />
          {amount != null && amount > 0 && (
            <DetailRow label={t('cards.amount')} value={`$${fmtAmt(amount)}`} />
          )}
        </div>

        {supportTelegram ? (
          <button
            type="button"
            onClick={() => {
              window.Telegram?.WebApp?.openTelegramLink(supportTelegram);
            }}
            className="flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
          >
            {t('cards.topUpManualContact')}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          className={`flex h-14 items-center justify-center rounded-full text-[15px] font-medium active:opacity-90 ${
            supportTelegram
              ? 'bg-black/[0.06] text-ink'
              : 'bg-ink text-white'
          }`}
        >
          {t('cards.close')}
        </button>
      </div>
    </BottomSheet>
  );
}

function DetailRow({
  label,
  value,
  onClick,
  accent,
}: {
  label: string;
  value: string;
  onClick?: () => void;
  accent?: boolean;
}) {
  const inner = (
    <>
      <span className="text-[13px] text-muted">{label}</span>
      <span className={`text-[14px] font-medium ${accent ? 'text-[#377dff]' : 'text-ink'}`}>
        {value}
      </span>
    </>
  );
  const cls =
    'flex items-center justify-between border-b border-black/[0.06] px-4 py-3 last:border-b-0';
  return onClick ? (
    <button type="button" onClick={onClick} className={`${cls} w-full text-left active:opacity-70`}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}
